import { memo, useState, useCallback } from 'react';
import { Handle, Position, NodeProps, useReactFlow } from 'reactflow';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { NodeData } from '@/types/workflow';
import { Target, Copy, Trash2 } from 'lucide-react';

interface LeadScoreNodeProps extends NodeProps {
  data: NodeData;
}

const scoringFactors = [
  { id: 'engagement', label: 'Engagement Level', description: 'Email opens, clicks, website visits' },
  { id: 'company_size', label: 'Company Size', description: 'Number of employees' },
  { id: 'industry', label: 'Industry Match', description: 'Target industry alignment' },
  { id: 'budget', label: 'Budget Indicators', description: 'Financial capability signals' },
  { id: 'authority', label: 'Decision Authority', description: 'Decision-making power' },
  { id: 'timeline', label: 'Purchase Timeline', description: 'Urgency to buy' },
  { id: 'behavior', label: 'Website Behavior', description: 'Pages visited, time spent' },
  { id: 'social', label: 'Social Signals', description: 'LinkedIn activity, social engagement' }
];

export const LeadScoreNode = memo(({ data, selected, id }: LeadScoreNodeProps) => {
  const [showToolbar, setShowToolbar] = useState(false);
  const { getNodes, setNodes, getEdges, setEdges } = useReactFlow();
  const nodeColor = '#8b5cf6'; // Purple for AI scoring

  const updateNodeData = useCallback((updates: Partial<NodeData>) => {
    const nodes = getNodes();
    const updatedNodes = nodes.map(node => {
      if (node.id === id) {
        return {
          ...node,
          data: {
            ...node.data,
            ...updates
          }
        };
      }
      return node;
    });
    setNodes(updatedNodes);
  }, [id, getNodes, setNodes]);

  const handleDuplicate = useCallback(() => {
    const nodes = getNodes();
    const currentNode = nodes.find(node => node.id === id);
    if (currentNode) {
      const newNode = {
        ...currentNode,
        id: `${currentNode.type}_${Date.now()}`,
        position: {
          x: currentNode.position.x + 50,
          y: currentNode.position.y + 50,
        },
      };
      setNodes([...nodes, newNode]);
    }
  }, [id, getNodes, setNodes]);

  const handleDelete = useCallback(() => {
    const nodes = getNodes();
    const edges = getEdges();
    
    const updatedNodes = nodes.filter(node => node.id !== id);
    const updatedEdges = edges.filter(edge => edge.source !== id && edge.target !== id);
    
    setNodes(updatedNodes);
    setEdges(updatedEdges);
  }, [id, getNodes, getEdges, setNodes, setEdges]);

  const handleFactorToggle = (factorId: string, checked: boolean) => {
    const currentFactors = data.scoringFactors || ['engagement', 'company_size', 'industry'];
    const updatedFactors = checked 
      ? [...currentFactors, factorId]
      : currentFactors.filter(f => f !== factorId);
    updateNodeData({ scoringFactors: updatedFactors });
  };

  return (
    <div 
      className="relative group"
      onMouseEnter={() => setShowToolbar(true)}
      onMouseLeave={() => setShowToolbar(false)}
    >
      {/* Toolbar */}
      {(showToolbar || selected) && (
        <div className="absolute -top-10 left-1/2 -translate-x-1/2 z-10">
          <div className="flex items-center gap-1 bg-background/95 backdrop-blur-sm border rounded-lg shadow-lg px-2 py-1">
            <Button
              size="sm"
              variant="ghost"
              className="h-6 w-6 p-0 hover:bg-muted"
              onClick={handleDuplicate}
            >
              <Copy className="w-3 h-3" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-6 w-6 p-0 hover:bg-destructive hover:text-destructive-foreground"
              onClick={handleDelete}
            >
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        </div>
      )}

      <Card 
        className={`min-w-[320px] backdrop-blur-md bg-background/75 border-purple-500/30 transition-all duration-200 ${
          selected 
            ? 'ring-2 ring-purple-500/50 shadow-lg shadow-purple-500/20' 
            : 'shadow-md hover:shadow-lg'
        }`}
      >
        <CardHeader className="pb-2">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center">
              <Target className="w-4 h-4 text-purple-400" />
            </div>
            <div className="flex-1">
              <div className="font-medium text-sm">{data.label || 'Score Lead'}</div>
              <div className="text-xs text-muted-foreground">AI-powered lead scoring</div>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="pt-0 space-y-3">
          <div>
            <Label className="text-xs">Custom Scoring Criteria</Label>
            <Textarea
              placeholder="Add custom scoring instructions for AI to consider..."
              value={data.customCriteria || ''}
              onChange={(e) => updateNodeData({ customCriteria: e.target.value })}
              className="min-h-[60px] resize-none text-xs"
            />
          </div>

          <div>
            <Label className="text-xs">Scoring Factors</Label>
            <div className="grid grid-cols-2 gap-1 mt-2 max-h-32 overflow-y-auto">
              {scoringFactors.map((factor) => (
                <div key={factor.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={factor.id}
                    checked={(data.scoringFactors || ['engagement', 'company_size', 'industry']).includes(factor.id)}
                    onCheckedChange={(checked) => handleFactorToggle(factor.id, checked as boolean)}
                  />
                  <Label htmlFor={factor.id} className="text-xs cursor-pointer">
                    {factor.label}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          <div className="p-2 bg-purple-50 dark:bg-purple-950/20 rounded-md border border-purple-200 dark:border-purple-800">
            <div className="text-xs text-purple-700 dark:text-purple-300">
              🎯 AI will analyze lead data and assign a score from 0-100 based on selected factors
            </div>
          </div>
        </CardContent>
        
        {/* Input Handle */}
        <Handle
          type="target"
          position={Position.Left}
          id="lead"
          className="custom-handle"
        />
        
        {/* Output Handle */}
        <Handle
          type="source"
          position={Position.Right}
          id="score"
          className="custom-handle"
          style={{
            backgroundColor: '#8b5cf6',
            boxShadow: '0 2px 8px rgba(139, 92, 246, 0.3)',
          }}
        />
      </Card>
    </div>
  );
});

LeadScoreNode.displayName = 'LeadScoreNode';